<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-ranking-star me-2" style="color: #f59e0b;"></i>{{ $t('Product Sales') }}
          </h5>
          <small class="text-muted">{{ $t('Products & Menu') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Charts -->
    <div class="row g-3 mb-4">
      <div class="col-12 col-lg-5">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 py-3">
            <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-pie me-2 text-info"></i>{{ $t('Sales by Category') }}</h6>
          </div>
          <div class="card-body pt-0">
            <apexchart ref="categoryChart" type="pie" height="280" :options="categoryChartOptions" :series="categoryChartSeries" />
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-7">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 py-3">
            <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-ranking-star me-2 text-warning"></i>{{ $t('Top 10 Products') }}</h6>
          </div>
          <div class="card-body pt-0">
            <apexchart ref="productChart" type="bar" height="280" :options="productChartOptions" :series="productChartSeries" />
          </div>
        </div>
      </div>
    </div>

    <!-- Detail Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-list me-2 text-secondary"></i>{{ $t('Product Sales Detail') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">#</th>
                <th>{{ $t('Product') }}</th>
                <th>{{ $t('Category') }}</th>
                <th class="text-end">{{ $t('Qty Sold') }}</th>
                <th class="text-end">{{ $t('Revenue') }}</th>
                <th class="text-end pe-3">{{ $t('% of Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!productTableData.length && !loading">
                <td colspan="6" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(p, i) in productTableData" :key="p.product_id || i">
                <td class="ps-3">{{ i + 1 }}</td>
                <td class="fw-medium">{{ $ln(p) }}</td>
                <td><span class="badge bg-light text-dark">{{ $t(p.category) }}</span></td>
                <td class="text-end">{{ p.qty }}</td>
                <td class="text-end">{{ formatCurrency(p.revenue) }}</td>
                <td class="text-end pe-3">
                  <div class="d-flex align-items-center justify-content-end gap-2">
                    <div class="progress flex-grow-1" style="height: 5px; max-width: 80px;">
                      <div class="progress-bar bg-primary" :style="{ width: p.percent + '%' }"></div>
                    </div>
                    <span class="small">{{ p.percent }}%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

const CATEGORY_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#06b6d4', '#ef4444', '#64748b'];

export default {
  name: 'ReportProductSales',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Product Sales',
      products: [],
      categories: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.productSales(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches   = filter.branches || [];
      this.products   = payload.products || [];
      this.categories = payload.categories || [];
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const [catImg, prodImg] = await Promise.all([
        this.getChartImage('categoryChart'),
        this.getChartImage('productChart'),
      ]);
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Product Sales'), style: 'sectionTitle' },
      ];
      if (catImg || prodImg) {
        const cols = [];
        if (catImg) cols.push({ image: catImg, width: 240 });
        if (prodImg) cols.push({ image: prodImg, width: 260 });
        content.push({ columns: cols, columnGap: 10, margin: [0, 0, 0, 12] });
      }
      content.push(
        { text: this.$t('Product Sales Detail'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: this.$t('Product'), style: 'tableHeader' },
                { text: this.$t('Category'), style: 'tableHeader' },
                { text: this.$t('Qty Sold'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Revenue'), style: 'tableHeader', alignment: 'right' },
                { text: '% ' + this.$t('of Total'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.productTableData.map((p, i) => [
                { text: String(i + 1), style: 'tableCell' },
                { text: p.name, style: 'tableCell' },
                { text: p.category, style: 'tableCell' },
                { text: String(p.qty), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(p.revenue), style: 'tableCell', alignment: 'right' },
                { text: `${p.percent}%`, style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    productTableData() {
      return this.products || [];
    },
    topProducts() {
      return this.productTableData.slice(0, 10);
    },
    categoryChartSeries() {
      return this.categories.map(c => c.revenue || 0);
    },
    categoryChartOptions() {
      return {
        chart: { fontFamily: 'Sarabun, sans-serif' },
        labels: this.categories.map(c => this.$ln(c)),
        colors: CATEGORY_COLORS,
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: (v) => `${Number(v).toFixed(0)}%` },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
      };
    },
    productChartSeries() {
      return [{ name: this.$t('Qty Sold'), data: this.topProducts.map(p => p.qty || 0) }];
    },
    productChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        xaxis: { categories: this.topProducts.map(p => this.$ln(p)) },
        colors: ['#3b82f6'],
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
